import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import referralService from '../../services/referralService';
import {
  ClipboardList,
  CheckCircle,
  DollarSign,
  Users
} from 'lucide-react';
import LoadingSpinner from '../LoadingSpinner';

const ReferralProgram = () => {
  const { currentUser } = useAuth();
  const [referralCode, setReferralCode] = useState('');
  const [referrals, setReferrals] = useState([]);
  const [earnings, setEarnings] = useState(0);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sendStatus, setSendStatus] = useState(null);

  useEffect(() => {
    const fetchReferralData = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        
        // Get user's referral code from Firestore
        const code = await referralService.getUserReferralCode(currentUser.uid);
        setReferralCode(code);
        
        // Get user's referrals from Firestore
        const userReferrals = await referralService.getUserReferrals(currentUser.uid);
        setReferrals(userReferrals);
        
        // Calculate total earnings from referrals
        const totalEarnings = await referralService.calculateTotalEarnings(currentUser.uid);
        setEarnings(totalEarnings);
        
      } catch (error) {
        console.error("Error fetching referral data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchReferralData();
  }, [currentUser]);

  const handleCopyReferralLink = () => {
    const referralLink = `https://engagedmanila.com/register?ref=${referralCode}`;
    navigator.clipboard.writeText(referralLink)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    
    if (!inviteEmail.trim()) {
      setSendStatus('error');
      return;
    }
    
    setSending(true);
    setSendStatus(null);
    
    try {
      // Send invitation using referral service
      await referralService.sendReferralInvitation(
        currentUser.uid,
        referralCode,
        inviteEmail,
        inviteMessage
      );
      
      setSendStatus('success');
      setInviteEmail('');
      setInviteMessage('');
      
      // Close the form after successful send
      setTimeout(() => {
        setShowInviteForm(false);
        setSendStatus(null);
      }, 2000);
      
    } catch (error) {
      console.error("Error sending invitation:", error);
      setSendStatus('error');
    } finally {
      setSending(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'registered':
        return 'bg-blue-100 text-blue-800';
      case 'active':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'registered':
        return 'Registered';
      case 'active':
        return 'Active';
      case 'completed':
        return 'Completed';
      default:
        return status;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Referral Program Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-800">Referral Program</h3>
      </div>
      
      <div className="p-6">
        {/* Referral Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center">
              <div className="bg-primary bg-opacity-10 p-3 rounded-full">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Referrals</p>
                <p className="text-xl font-semibold">{referrals.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center">
              <div className="bg-green-100 p-3 rounded-full">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Successful Referrals</p>
                <p className="text-xl font-semibold">{referrals.filter(r => r.status === 'completed').length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center">
              <div className="bg-purple-100 p-3 rounded-full">
                <DollarSign className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Earnings</p>
                <p className="text-xl font-semibold">₱{earnings.toLocaleString('en-PH')}</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Referral Link */}
        <div className="bg-gray-50 rounded-lg p-6 mb-8">
          <h4 className="text-md font-medium text-gray-900 mb-2">Your Referral Link</h4>
          <p className="text-sm text-gray-600 mb-4">
            Share this link with other influencers. You'll earn ₱500 for each successful referral once they complete their first campaign.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-grow">
              <div className="flex items-center">
                <div className="bg-white border border-gray-300 rounded-l-md px-4 py-2 text-gray-500 w-full h-10 flex items-center">
                  <span className="text-sm truncate">https://engagedmanila.com/register?ref={referralCode}</span>
                </div>
                <button
                  onClick={handleCopyReferralLink}
                  className="inline-flex items-center justify-center bg-primary text-white px-4 py-2 h-10 rounded-r-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  {copied ? (
                    <CheckCircle className="h-5 w-5" />
                  ) : (
                    <ClipboardList className="h-5 w-5" />
                  )}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {copied ? 'Copied to clipboard!' : 'Click to copy your referral link'}
              </p>
            </div>
            
            <button
              onClick={() => setShowInviteForm(!showInviteForm)}
              className="inline-flex items-center justify-center px-4 py-2 h-10 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Invite by Email
            </button>
          </div>
        </div>
        
        {/* Email Invitation Form */}
        {showInviteForm && (
          <div className="bg-gray-50 rounded-lg p-6 mb-8 border border-gray-200">
            <h4 className="text-md font-medium text-gray-900 mb-2">Invite a Friend</h4>
            
            <form onSubmit={handleSendInvite}>
              <div className="mb-4">
                <label htmlFor="invite-email" className="block text-sm font-medium text-gray-700 mb-1">
                  Friend's Email
                </label>
                <input
                  type="email"
                  id="invite-email"
                  className="block w-full rounded-md border border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  placeholder="email@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  required
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="invite-message" className="block text-sm font-medium text-gray-700 mb-1">
                  Personal Message (Optional)
                </label>
                <textarea
                  id="invite-message"
                  rows="3"
                  className="block w-full rounded-md border border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  placeholder="Hey! I'm using Engaged Manila to find review opportunities and earn money. Join using my referral link!"
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                ></textarea>
              </div>
              
              {sendStatus && (
                <div className={`p-4 mb-4 rounded-md ${sendStatus === 'success' ? 'bg-green-50' : 'bg-red-50'}`}>
                  <div className="flex">
                    <div className="flex-shrink-0">
                      {sendStatus === 'success' ? (
                        <CheckCircle className="h-5 w-5 text-green-400" />
                      ) : (
                        <CheckCircle className="h-5 w-5 text-red-400" />
                      )}
                    </div>
                    <div className="ml-3">
                      <p className={`text-sm ${sendStatus === 'success' ? 'text-green-700' : 'text-red-700'}`}>
                        {sendStatus === 'success' 
                          ? 'Invitation sent successfully!' 
                          : 'Failed to send invitation. Please try again.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowInviteForm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sending}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
                >
                  {sending ? (
                    'Sending...'
                  ) : (
                    'Send Invitation'
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
        
        {/* Referral History */}
        <div>
          <h4 className="text-md font-medium text-gray-900 mb-4">Your Referrals</h4>
          
          {loading ? (
            <div className="flex justify-center items-center h-32">
        <LoadingSpinner size="xl" color="primary" />
      </div>
          ) : referrals.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bonus</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {referrals.map((referral) => (
                    <tr key={referral.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{referral.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{referral.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(referral.status)}`}>
                          {getStatusText(referral.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(referral.date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {referral.bonus > 0 ? `₱${referral.bonus.toLocaleString('en-PH')}` : '-'}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <Users className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No referrals yet</h3>
              <p className="mt-1 text-sm text-gray-500">
                Start inviting friends to earn referral bonuses.
              </p>
            </div>
          )}
        </div>
        
        {/* Referral Program Info */}
        <div className="mt-8 bg-blue-50 rounded-lg p-4 border border-blue-100">
          <h4 className="text-sm font-medium text-blue-800 mb-2">How the Referral Program Works</h4>
          <ol className="list-decimal list-inside text-sm text-blue-700 space-y-1">
            <li>Share your unique referral link with other influencers</li>
            <li>When they sign up using your link, they'll be connected to your account</li>
            <li>Once they complete their first campaign, you'll earn a ₱500 bonus</li>
            <li>There's no limit to how many people you can refer</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default ReferralProgram;
